
import { Component, Watch } from 'vue-property-decorator';
import { NavigationGuardNext, Route } from 'vue-router';
import { mixins } from 'vue-class-component';
import { TipTap } from '@/mixins/TipTap';
import * as ContentServices from '@/services/ContentServices';
import { ROOT_ACTIONS } from '@/store/actions';
import { describePath, isRecord, mergeWithDefaults, pathParts, replaceUntouched, validate, JsonSchema, ValidationError } from '@/content/schema';
import {
  ancestors, buildOutline, getAt, layoutAt, nodeFor, nodeId, nodePath, parentList, schemaAt, setAt, OutlineNode,
} from '@/content/outline';
import { emptyLegacy, pageMatches, sitePath, ContentPage, LegacyField, PageGroup, PAGE_GROUPS, PAGES, SITE_URL } from '@/content/pages';
import SchemaField from '@/components/content/SchemaField.vue';
import ContentOutline from '@/components/content/ContentOutline.vue';
import ContestEntriesDialog from '@/components/content/ContestEntriesDialog.vue';
import { askConfirm } from '@/content/confirm';
import { upgradeContent } from '@/content/upgrades';

/** An entry of the page picker: a group header or a page. */
type PageOption = { header: string } | { text: string; value: string; page: ContentPage };

interface Crumb {
  id: string;
  text: string;
}

@Component({ components: { ContestEntriesDialog, ContentOutline, SchemaField } })
export default class Index extends mixins(TipTap) {
  groups: PageGroup[] = PAGE_GROUPS;

  loading = { content: false, submit: false };

  /** Structured page: the whole blob, including keys the schema does not know. */
  structured: unknown = {};
  /** Older page: flat fields. */
  legacy: Record<string, LegacyField> = {};

  showJson = false;
  jsonDraft = '';
  jsonError = '';

  /** The content as loaded or last saved, to tell whether there are unsaved changes. */
  savedSnapshot = '';
  /** The same, parsed: the outline marks the parts that changed since. */
  savedValue: unknown = null;
  /** The outline as a dialog, on screens too narrow for two columns. */
  outlineDialog = false;
  private loadCount = 0;
  /** The page whose content is in `structured` or `legacy`; behind `current` while a new page loads. */
  private loadedSlug = '';

  /** The page in the address (`/content?pagina=over-ons`), so a reload or a shared link opens it. */
  get current(): ContentPage {
    const slug = this.$route.query.pagina;
    return PAGES.find((page) => page.slug === slug) || PAGES[0];
  }

  get pageOptions(): PageOption[] {
    return this.groups.flatMap((group) => [{ header: group.title }, ...group.pages.map((page) => ({ text: page.name, value: page.slug, page }))]);
  }

  /** The picker finds a page by its name and by the rubrieken it holds; headers drop out, as without a filter. */
  findPage(option: PageOption, search: string): boolean {
    return 'page' in option && pageMatches(option.page, search);
  }

  get siteUrl(): string {
    const path = this.loaded ? sitePath(this.current, this.structured) : '';
    return path ? `${SITE_URL}${path}` : '';
  }

  /** Whether the content on screen belongs to the chosen page. */
  get loaded(): boolean {
    return this.loadedSlug === this.current.slug;
  }

  get errors(): ValidationError[] {
    return this.current.definition && this.loaded ? validate(this.current.definition.schema, this.structured) : [];
  }

  /** The parts of the page; empty for a short page, which is one form. */
  get outline(): OutlineNode[] {
    return this.current.definition ? buildOutline(this.current.definition.schema, this.structured) : [];
  }

  /** The part in the address (`&pad=partners.supporters[2]`), or the nearest part that holds it; the first part by default. */
  get activeNode(): OutlineNode {
    const pad = typeof this.$route.query.pad === 'string' ? this.$route.query.pad : '';
    return (pad && nodeFor(this.outline, nodePath(pad))) || this.outline[0];
  }

  get activePath(): string {
    return nodePath(this.activeNode.id);
  }

  get paneSchema(): JsonSchema {
    const definition = this.current.definition;
    return (definition && schemaAt(definition.schema, this.activePath)) || {};
  }

  get paneValue(): unknown {
    return this.activePath ? getAt(this.structured, this.activePath) : this.structured;
  }

  /** The key of the part and of the group around it: some fields are recognised by them (a technique's name, factcheck ids). */
  get paneKey(): string {
    const parts = pathParts(this.activePath).filter((part): part is string => typeof part === 'string');
    return parts[parts.length - 1] || '';
  }

  get paneParentKey(): string {
    const parts = pathParts(this.activePath).filter((part): part is string => typeof part === 'string');
    return parts[parts.length - 2] || '';
  }

  get paneLayout(): string {
    return layoutAt(this.structured, this.activePath);
  }

  get crumbs(): Crumb[] {
    const byId = new Map(this.flatOutline.map((node) => [node.id, node]));
    const trail = [...ancestors(this.outline, this.activeNode.id), this.activeNode.id];
    return trail.map((id) => ({ id, text: byId.get(id)?.name || '' }));
  }

  get flatOutline(): OutlineNode[] {
    const walk = (nodes: OutlineNode[]): OutlineNode[] => nodes.flatMap((node) => [node, ...walk(node.children || [])]);
    return walk(this.outline);
  }

  /** Where a list item sits in its list, for the previous and next buttons. */
  get itemPosition(): { list: string; index: number; count: number } | null {
    const position = this.activeNode.kind === 'item' ? parentList(this.activePath) : null;
    if (!position) return null;
    const list = getAt(this.structured, position.list);
    return { ...position, count: Array.isArray(list) ? list.length : 0 };
  }

  get problemCount(): string {
    return this.errors.length === 1 ? '1 probleem' : `${this.errors.length} problemen`;
  }

  get snapshot(): string {
    return JSON.stringify(this.current.definition ? this.structured : this.legacy);
  }

  get dirty(): boolean {
    return !this.loading.content && this.snapshot !== this.savedSnapshot;
  }

  mounted(): void {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  beforeDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
    if (await this.confirmDiscard()) next();
    else next(false);
  }

  /** Another page of this view, also through the browser's back button. */
  async beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
    if (to.query.pagina === from.query.pagina || (await this.confirmDiscard())) next();
    else next(false);
  }

  selectPage(slug: string | null): void {
    if (!slug || slug === this.current.slug) return;
    this.$router.push({ query: { pagina: slug } }).catch(() => undefined);
  }

  /** Opens a part of the page; with a search, the first field that holds it gets the focus. */
  selectNode(id: string, search = ''): void {
    this.outlineDialog = false;
    this.openPath(nodePath(id));
    if (search) this.$nextTick(() => this.focusText(search, 20));
  }

  openPath(path: string): void {
    const pad = nodeId(path);
    if (pad === this.$route.query.pad || pad === this.activeNode.id) return;
    this.$router.push({ query: { ...this.$route.query, pad } }).catch(() => undefined);
    // A part opened from far down the page starts at its top.
    const pane = this.$refs.pane as { $el?: Element } | undefined;
    if (pane && pane.$el && pane.$el.getBoundingClientRect().top < 0) pane.$el.scrollIntoView({ block: 'start' });
  }

  setPane(next: unknown): void {
    this.structured = this.activePath ? setAt(this.structured, this.activePath, next) : next;
  }

  stepItem(step: number): void {
    const position = this.itemPosition;
    if (!position) return;
    this.openPath(`${position.list}[${position.index + step + 1}]`);
  }

  async removeActiveItem(): Promise<void> {
    const position = this.itemPosition;
    if (!position) return;
    const name = this.activeNode.name;
    if (!(await askConfirm({ title: `"${name}" verwijderen?`, text: 'Het verdwijnt pas van de site nadat je opslaat.', confirmLabel: 'Verwijderen', danger: true }))) return;
    const list = getAt(this.structured, position.list);
    if (!Array.isArray(list)) return;
    this.openPath(position.list);
    this.structured = setAt(this.structured, position.list, list.filter((_, index) => index !== position.index));
  }

  /** Puts the focus on the first field of the open part whose text holds the search. */
  private focusText(search: string, attempts: number): void {
    const words = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/\s+/).filter(Boolean);
    const fields = Array.from(this.$el.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('.pane input, .pane textarea, .pane .ProseMirror'));
    const text = (field: Element): string => ('value' in field ? (field as HTMLInputElement).value : field.textContent || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const hit = fields.find((field) => words.some((word) => text(field).includes(word)));
    if (hit instanceof HTMLElement) {
      hit.scrollIntoView({ behavior: 'smooth', block: 'center' });
      hit.focus({ preventScroll: true });
      return;
    }
    if (attempts > 0) window.setTimeout(() => this.focusText(search, attempts - 1), 50);
  }

  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.dirty) return;
    event.preventDefault();
    event.returnValue = '';
  }

  /** True when there is nothing unsaved, or the editor agrees to lose it. */
  async confirmDiscard(): Promise<boolean> {
    if (!this.dirty) return true;
    return askConfirm({
      title: 'Wijzigingen niet opgeslagen',
      text: `Je wijzigingen aan "${this.current.name}" zijn nog niet opgeslagen. Toch verdergaan en ze weggooien?`,
      confirmLabel: 'Weggooien',
      danger: true,
    });
  }

  get currentSlug(): string {
    return this.current.slug;
  }

  /**
   * Loads a page when another page is chosen. Watching the slug, not the page object: Vue calls an
   * object watcher on every change of the address, which reloaded the saved content (losing unsaved
   * edits and undoing a delete) each time the editor opened another part of the same page.
   */
  @Watch('currentSlug', { immediate: true })
  async onPageChange(): Promise<void> {
    const request = ++this.loadCount;
    this.loading.content = true;
    this.showJson = false;
    const page = this.current;
    const result = await ContentServices.GetContent(page.slug);
    // A later page switch wins over a slow answer for an earlier one.
    if (request !== this.loadCount) return;
    const saved: unknown = upgradeContent(page.slug, result ? result.content : null);
    if (page.definition) {
      this.structured = mergeWithDefaults(page.definition.schema, replaceUntouched(page.definition.schema, saved, page.definition), page.definition.defaults);
    } else {
      this.legacy = { ...emptyLegacy(page), ...(isRecord(saved) ? (saved as Record<string, LegacyField>) : {}) };
    }
    this.savedSnapshot = this.snapshot;
    this.savedValue = page.definition ? JSON.parse(this.savedSnapshot) : null;
    this.loadedSlug = page.slug;
    this.loading.content = false;
  }

  describe(path: string): string {
    return describePath(path, this.structured);
  }

  /** Opens the part that holds an error and scrolls its field into view. */
  revealError(path: string): void {
    if (this.showJson) {
      this.applyJson();
      this.showJson = false;
    }
    const node = nodeFor(this.outline, path);
    if (node) this.openPath(nodePath(node.id));
    this.$nextTick(() => this.scrollTo(path, 20));
  }

  /** The part renders after the address changes, so the field can take a few frames to exist. */
  private scrollTo(path: string, attempts: number): void {
    const field = this.$el.querySelector(`[data-path="${CSS.escape(path)}"]`);
    if (field instanceof HTMLElement) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (field.matches('input, textarea')) field.focus({ preventScroll: true });
      return;
    }
    if (attempts > 0) window.setTimeout(() => this.scrollTo(path, attempts - 1), 50);
  }

  @Watch('showJson')
  onToggleJson(open: boolean): void {
    if (open) {
      this.jsonDraft = JSON.stringify(this.structured, null, 2);
      this.jsonError = '';
    }
  }

  applyJson(): void {
    try {
      this.structured = JSON.parse(this.jsonDraft);
      this.jsonError = '';
    } catch (error) {
      this.jsonError = 'Geen geldige JSON';
    }
  }

  async resetToDefaults(): Promise<void> {
    const definition = this.current.definition;
    if (!definition) return;
    const go = await askConfirm({ title: 'Standaardtekst herstellen?', text: 'Alle velden van deze pagina gaan terug naar de standaardtekst van de website. Pas na opslaan staat dat op de site.', confirmLabel: 'Herstellen', danger: true });
    if (!go) return;
    this.structured = mergeWithDefaults(definition.schema as JsonSchema, null, definition.defaults);
    if (this.showJson) this.jsonDraft = JSON.stringify(this.structured, null, 2);
  }

  async save(): Promise<void> {
    if (this.showJson) this.applyJson();
    if (this.jsonError) return;
    if (this.errors.length) {
      this.revealError(this.errors[0].path);
      return;
    }
    try {
      this.loading.submit = true;
      const snapshot = this.snapshot;
      await ContentServices.SaveContent(this.current.slug, this.current.definition ? this.structured : this.legacy);
      this.savedSnapshot = snapshot;
      this.savedValue = this.current.definition ? JSON.parse(snapshot) : null;
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'success', text: 'Content opgeslagen' });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Er is iets mis gegaan met het opslaan van de content' });
    }
    this.loading.submit = false;
  }
}
